/* eslint-disable react-hooks/exhaustive-deps */
import React, { useMemo } from 'react';
import { useFormik } from 'formik';
import * as yup from 'yup';
import * as validation from '@constants/validation/validation';

import { Box, Grid, Button } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import SearchIcon from '@material-ui/icons/Search';
import { styled } from '@material-ui/styles';

import { Wrapper } from '@shared/Wrapper';
import { CustomTextField } from '@components/common/CustomTextField';
import { CustomHelperText } from '@components/common/CustomHelperText';
import { isMobile } from '@utils/mobileDetect';
import { useCompanyAutocomplete } from 'src/hooks/useCompanyAutocomplete';

import { Auth } from 'src/api/requests';
import { showErrorNotification } from 'src/components/common/Notifications';
import { handleInvalidError } from 'src/utils/handleInvalidError';
import { useHistory } from 'react-router-dom';
import { successLogin } from 'src/store/thunks/auth';
import { useDispatch } from 'react-redux';
import { err } from 'src/constants/messages';
import { useStyles } from '../StepTwo/styles';

export const StepThree = ({ credential }) => {
  const classes = useStyles();
  const { setCompanyQuery, companyQuery, companyOptions, companyIsLoading } = useCompanyAutocomplete();
  const history = useHistory();
  const dispatch = useDispatch();

  const formik = useFormik({
    initialValues: {
      tile: '',
      company: undefined,
      company_name: undefined,
      company_uen: undefined,
    },
    validationSchema: yup.object().shape({
      tile: validation.TILE,
      company: yup.mixed().test('company-required', 'Please choose your company or add new', val => val !== undefined),
      company_name: yup.string().when('company', {
        is: company => company === null,
        then: validation.COMPANY,
        otherwise: yup.string(),
      }),
      company_uen: yup.string().when('company', {
        is: company => company === null,
        then: validation.COMPANY,
        otherwise: yup.string(),
      }),
    }),
    onSubmit: values => {
      Auth.googleAuth({ token: credential, ...values })
        .then(res => {
          if (res.success) {
            history.push('/account/dashboard');
            dispatch(successLogin(res.data));
          }
        })
        .catch(e => {
          formik.setSubmitting(false);
          if (e.response.status === 422) {
            handleInvalidError(formik.setErrors)(e);
          } else if (typeof e.response.data.message === 'string') {
            showErrorNotification(e.response.data.message, 5000);
          } else {
            showErrorNotification(err.DEFAULT, 5000);
          }
        });
    },
  });

  const selectedCompanyOption = useMemo(() => {
    if (formik.values.company == null) return null;
    return companyOptions.find(option => option.id.toString() === formik.values.company.toString()) ?? null;
  }, [companyOptions, formik.values.company]);

  const handleSearchCompany = (e, value) => {
    const query = value ?? e?.currentTarget?.value ?? '';
    setCompanyQuery(query);
    if ((query === '' || query == null) && formik.values.company !== null) {
      formik.setFieldValue('company', undefined, false);
    }
  };

  return (
    <form onSubmit={formik.handleSubmit}>
      <Box className={classes.stepTwo}>
        <Grid container spacing={isMobile ? 3 : 5}>
          <Grid item xs={12}>
            <Wrapper
              padding={isMobile ? '24px 0px 0px' : '40px'}
              shadow={isMobile ? 'false' : '4px 8px 24px rgba(0, 0, 0, 0.04)'}
              borderRadius="8px"
            >
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <CustomTextField
                    type="text"
                    name="tile"
                    placeholder="Position *"
                    onBlur={formik.handleBlur}
                    value={formik.values.tile}
                    helpertextposabsl="true"
                    onChange={formik.handleChange}
                    helperText={formik.touched.tile && formik.errors.tile}
                    error={Boolean(formik.touched.tile && formik.errors.tile)}
                  />
                </Grid>

                {formik.values.company !== null && (
                  <Grid item xs={12}>
                    <AutoCompleteWrapper error={Boolean(formik.touched.company && formik.errors.company).toString()}>
                      <Autocomplete
                        value={selectedCompanyOption}
                        inputValue={companyQuery}
                        options={companyOptions}
                        loading={companyIsLoading}
                        getOptionLabel={option => (option ? option.name : '')}
                        noOptionsText={
                          formik.values.company === null ? undefined : (
                            <Button
                              onMouseDown={e => e.preventDefault()}
                              onClick={() => {
                                setCompanyQuery('');
                                formik.setFieldValue('company', null);
                              }}
                            >
                              <span role="img" aria-label="add">
                                ➕
                              </span>
                              <span> Add new company</span>
                            </Button>
                          )
                        }
                        renderInput={params => (
                          <div ref={params.InputProps.ref} className={classes.inputContainer}>
                            <SearchIcon className={classes.icon} />
                            <input
                              type="text"
                              {...params.inputProps}
                              name="company"
                              placeholder="Select company"
                              className={classes.searchAutocompleteInput}
                            />
                          </div>
                        )}
                        onBlur={() => formik.setTouched({ ...formik.touched, company: true })}
                        onChange={(e, value) => {
                          if (value) {
                            setCompanyQuery(value.name);
                            formik.setFieldValue('company', value.id, true);
                            formik.setFieldValue('company_name', undefined, false);
                            formik.setFieldValue('company_uen', undefined, false);
                          }
                        }}
                        onInputChange={handleSearchCompany}
                      />
                    </AutoCompleteWrapper>
                    {formik.touched.company && formik.errors.company && (
                      <CustomHelperText>{formik.errors.company}</CustomHelperText>
                    )}
                  </Grid>
                )}

                {formik.values.company === null && (
                  <>
                    <Grid item xs={12} md={6}>
                      <CustomTextField
                        type="text"
                        name="company_name"
                        placeholder="Your company name"
                        value={formik.values.company_name}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        className={classes.input}
                        helperText={formik.touched.company_name && formik.errors.company_name}
                        error={Boolean(formik.touched.company_name && formik.errors.company_name)}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <CustomTextField
                        type="text"
                        name="company_uen"
                        placeholder="Company registration number"
                        value={formik.values.company_uen}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        className={classes.input}
                        helperText={formik.touched.company_uen && formik.errors.company_uen}
                        error={Boolean(formik.touched.company_uen && formik.errors.company_uen)}
                      />
                    </Grid>
                  </>
                )}

                <Grid item xs={12}>
                  <Button size="large" type="submit" color="primary" variant="contained" disabled={!formik.isValid}>
                    Finish registration
                  </Button>
                </Grid>
              </Grid>
            </Wrapper>
          </Grid>
        </Grid>
      </Box>
    </form>
  );
};

const AutoCompleteWrapper = styled(Box)({
  '& input': {
    borderColor: ({ error }) => error === 'true' && '#f44336',
  },
});
